import React from 'react';
import Box from '@mui/material/Box';
import { type Interaction } from '@lib/models/Interaction';
import { useAiChatServices } from '..';
import Avatar from '@mui/material/Avatar';
import AgentAvatar from './AgentAvatar';

interface MessagePersonaProps {
  isUser: boolean;
  authenticated?: boolean;
  message: Interaction;
  tone: 'chrome' | 'light'
}

const MONO = 'ui-monospace, SFMono-Regular, Menlo, "JetBrains Mono", monospace';

/**
 * Left indent (px) that aligns a message body under the persona label instead of the avatar:
 * avatar width (30) + the persona row `gap: 1` (8px). Message bodies apply this when `showPersona`
 * is on so the paragraph text starts after the avatar, matching the header row.
 */
export const PERSONA_CONTENT_INDENT = 38;

function defaultFormatTimestamp(date: Date): string {
  try {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

/**
 * Persona header row above a message — an avatar + "{NAME} · {time}". Rendered only when the host
 * enables `showPersona`. The label/initials come from `resolvePersona` (default: generic
 * "Você" / "Agente") and the time from `formatTimestamp(message.created)` (default: locale HH:mm).
 */
const MessagePersona: React.FC<MessagePersonaProps> = ({ isUser, message, tone,authenticated }) => {
  const { showPersona, formatTimestamp, resolvePersona } = useAiChatServices();
  if (!showPersona) return null;

  const onChrome = tone === 'chrome';

  const persona = resolvePersona?.({ isUser, message })
    ?? { label: isUser ? 'Você' : 'Agente', initials: isUser ? 'V' : 'A' };
  const initials = persona.initials ?? (persona.label.slice(0, 1).toUpperCase() || '·');
  const time = (formatTimestamp ?? defaultFormatTimestamp)(message.created);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, mb: 0.5 }}>
      {/* <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: '50%',
          flexShrink: 0,
          display: 'grid',
          placeItems: 'center',
          fontSize: 11,
          fontWeight: 600,
          bgcolor: isUser ? 'text.primary' : 'primary.main',
          color: isUser ? 'background.paper' : 'primary.contrastText',
        }}
      >
        {initials}
      </Box> */}
      {authenticated ?
      <Avatar
        src={persona.picture || undefined}
        sx={{
          width: 30,
          height: 30,
          fontSize: 12,
          fontWeight: 500,
          flexShrink: 0,
          bgcolor: onChrome ? '#3A3935' : 'text.primary',
          color: onChrome ? '#fff' : 'primary.main',
        }}
      >
        {initials}
      </Avatar>
      :
        <AgentAvatar variant='marca' tone='chrome' />
      }
      <Box
        component="span"
        sx={{
          fontFamily: MONO,
          fontSize: 10,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: 'text.disabled',
        }}
      >
        {persona.label}{time ? ` · ${time}` : ''}
      </Box>
    </Box>
  );
};

export default MessagePersona;
